// src/components/PrepGuide/aiTalkingPoints.ts
// Fetches AI-curated talking point categories from the edge function,
// maps them to TalkingPointCategory[] so the existing UI renders unchanged.

import { supabase } from "../../lib/supabase";
import type { Case } from "../../types/caseView";
import type { TalkingPointCategory, TalkingPointPill } from "./talkingPointsEngine";

// ── AI response types (matches edge function output) ────────

interface AIChip {
  id: string;
  label: string;
  selected_by_default: boolean;
}

interface AICategory {
  id: string;
  title: string;
  subtitle: string | null;
  chips: AIChip[];
  collapsed_by_default: boolean;
}

interface AITalkingPointsResponse {
  categories: AICategory[];
  ui_notes: {
    max_default_selected: number;
    show_selected_first: boolean;
    hide_irrelevant: boolean;
  };
}

// ── Result type ─────────────────────────────────────────────

export interface AITalkingPointsResult {
  categories: TalkingPointCategory[];
  /** Pre-selected pill IDs per category (from AI's selected_by_default) */
  autoSelected: Record<string, string[]>;
  /** Raw AI response for debugging / future use */
  raw: AITalkingPointsResponse;
}

// ── Sentence prefix generator ───────────────────────────────

function deriveSentencePrefix(title: string): string {
  const t = title.toLowerCase();
  if (t.includes("fail") || t.includes("tried")) return "The patient has tried";
  if (t.includes("persist") || t.includes("duration") || t.includes("bleeding"))
    return "Symptoms have been present including";
  if (t.includes("rule") || t.includes("pathology") || t.includes("diagnostic"))
    return "We need to rule out";
  if (t.includes("risk") || t.includes("delay")) return "Without intervention, the patient risks";
  if (t.includes("guideline") || t.includes("criteria") || t.includes("standard"))
    return "Current guidelines support";
  if (t.includes("severity") || t.includes("impact") || t.includes("function"))
    return "The condition has resulted in";
  return "Clinical evidence supports";
}

// ── Map AI response → TalkingPointCategory[] ────────────────

function mapAIToCategories(ai: AITalkingPointsResponse): {
  categories: TalkingPointCategory[];
  autoSelected: Record<string, string[]>;
} {
  const categories: TalkingPointCategory[] = [];
  const autoSelected: Record<string, string[]> = {};

  ai.categories.forEach((aiCat, index) => {
    const pills: TalkingPointPill[] = aiCat.chips.map((chip) => ({
      id: chip.id,
      label: chip.label,
    }));

    const selected = aiCat.chips
      .filter((chip) => chip.selected_by_default)
      .map((chip) => chip.id);

    if (selected.length > 0) {
      autoSelected[aiCat.id] = selected;
    }

    categories.push({
      id: aiCat.id,
      label: aiCat.title,
      description: aiCat.subtitle || "",
      order: index,
      pills,
      sentencePrefix: deriveSentencePrefix(aiCat.title),
      autoExpand: !aiCat.collapsed_by_default,
    });
  });

  return { categories, autoSelected };
}

// ── Fetch from edge function ────────────────────────────────

export async function fetchAITalkingPoints(
  caseData: Case,
): Promise<AITalkingPointsResult> {
  const { data, error } = await supabase.functions.invoke(
    "generate-talking-points",
    { body: { caseData } },
  );

  if (error) {
    throw new Error(`Edge function error: ${error.message}`);
  }

  // The edge function returns the parsed AI JSON directly
  const aiResponse = data as AITalkingPointsResponse;

  if (!aiResponse?.categories) {
    throw new Error("Invalid AI response: missing categories");
  }

  const { categories, autoSelected } = mapAIToCategories(aiResponse);

  return { categories, autoSelected, raw: aiResponse };
}

// ── Cache key for localStorage ──────────────────────────────

function cacheKey(caseId: string): string {
  return `peerprep.aiTalkingPts.v1:${caseId}`;
}

// ── Fingerprint case data to detect meaningful changes ──────

function caseFingerprint(c: Case): string {
  return [
    c.request_type,
    c.request_details,
    c.diagnoses,
    c.tried_already,
    c.clinical_history,
    c.additional_context,
    c.comorbidities,
    c.specialty,
  ]
    .filter(Boolean)
    .join("|");
}

// ── Cached fetch: returns cached result if case hasn't changed ──

export async function fetchAITalkingPointsCached(
  caseData: Case,
): Promise<AITalkingPointsResult & { fromCache: boolean }> {
  const key = cacheKey(caseData.id);
  const fp = caseFingerprint(caseData);

  // Try cache
  try {
    const cached = localStorage.getItem(key);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed.fingerprint === fp && parsed.result) {
        return { ...parsed.result, fromCache: true };
      }
    }
  } catch {
    // Cache miss — proceed to fetch
  }

  // Fetch fresh
  const result = await fetchAITalkingPoints(caseData);

  // Cache it
  try {
    localStorage.setItem(
      key,
      JSON.stringify({ fingerprint: fp, result, ts: Date.now() }),
    );
  } catch {
    // Storage full — not critical
  }

  return { ...result, fromCache: false };
}